import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import {
  clearImpersonationStash,
  readImpersonationStash,
  writeImpersonationStash,
  type ImpersonationStash,
} from '@/lib/impersonation';

/**
 * Hook that drives the superadmin emulation flow:
 *   - `start()`: re-auth + audit insert + session swap into target's JWT
 *   - `end()`: close audit row + restore admin's session
 *   - `state`: current emulation context (or null)
 *
 * The hook keeps its source of truth in two places that must agree:
 *   1. sessionStorage stash (client-side)
 *   2. current_impersonation_context() RPC (server-side)
 * If the two disagree (stash tampered with, or stash present while no
 * open audit row exists), we clear the stash and report not-impersonating.
 *
 * The hook also listens for SIGNED_OUT events while a stash is present —
 * if the target signs out mid-session, we attempt to restore the admin.
 */

interface StartArgs {
  target_user_id: string;
  target_email: string;
  reason: string;
  password: string;
  read_only?: boolean;
  suppress_notification?: boolean;
  suppression_reason?: string;
}

/**
 * Error thrown by `start()` when the server refuses. Exposes the
 * server-side `error` discriminator (e.g. 'open_session_exists',
 * 'password_verification_failed') so callers can branch the UX —
 * notably to show a "recover and retry" affordance for the
 * open_session_exists case.
 */
export class ImpersonationStartError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ImpersonationStartError';
    this.code = code;
  }
}

interface ServerContext {
  audit_id: string;
  admin_user_id: string;
  started_at: string;
  read_only: boolean;
  reason: string;
}

export type ImpersonationState =
  | { active: false }
  | {
      active: true;
      audit_id: string;
      admin_email: string;
      target_email: string;
      target_user_id: string;
      started_at: string;
      read_only: boolean;
      reason: string;
    };

const FUNCTIONS_BASE = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/?$/, '') + '/functions/v1';

async function callServerContext(): Promise<ServerContext | null> {
  const { data, error } = await supabase.rpc('current_impersonation_context');
  if (error) {
    // The RPC body is correct and defensive (returns NULL when there's no
    // open row). The errors we see here are raised at the PostgREST gateway
    // BEFORE the function runs — auth-layer failures (PGRST3xx, e.g. an
    // expired or in-transition JWT) that happen during the session swap or
    // when the target's short-lived token lapses. reconcile() already treats
    // a null context as "not impersonating, clear the stash", which is the
    // correct response, so these are benign and must not page.
    //
    // Only capture genuinely unexpected errors, and wrap them in a real Error
    // — `error` is a PostgrestError (a plain object), so passing it straight
    // to captureError logs the unactionable "Object captured as exception
    // with keys: code, details, hint, message" instead of a useful message.
    const isAuthTransient =
      error.code?.startsWith('PGRST3') || /jwt/i.test(error.message ?? '');
    if (!isAuthTransient) {
      captureError(
        new Error(`current_impersonation_context failed: ${error.message} (${error.code})`),
        { feature: 'impersonation-context', code: error.code, details: error.details },
      );
    }
    return null;
  }
  return (data as ServerContext | null) ?? null;
}

export function useImpersonation() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ImpersonationState>({ active: false });
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);

  // On mount + on auth changes, reconcile stash + server context.
  useEffect(() => {
    let cancelled = false;

    async function reconcile() {
      const stash = readImpersonationStash();
      if (!stash) {
        if (!cancelled) setState({ active: false });
        return;
      }
      const serverCtx = await callServerContext();
      if (cancelled) return;
      if (!serverCtx || serverCtx.audit_id !== stash.audit_id) {
        // Stash is stale or lying. Clear it.
        clearImpersonationStash();
        setState({ active: false });
        return;
      }
      setState({
        active: true,
        audit_id: stash.audit_id,
        admin_email: stash.admin_email,
        target_email: stash.target_email,
        target_user_id: stash.target_user_id,
        started_at: stash.started_at,
        read_only: serverCtx.read_only,
        reason: serverCtx.reason,
      });
    }

    reconcile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Any auth state change is a signal to re-check. Don't act
      // synchronously here — let supabase finish its internal state
      // transition first.
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        setTimeout(() => {
          if (!cancelled) reconcile();
        }, 0);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const start = useCallback(
    async (args: StartArgs) => {
      setIsStarting(true);
      try {
        const { data: currentSession } = await supabase.auth.getSession();
        const adminSession = currentSession?.session;
        if (!adminSession?.user?.email || !adminSession.refresh_token) {
          throw new Error('No active admin session to stash.');
        }

        const startRes = await fetch(`${FUNCTIONS_BASE}/start-impersonation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminSession.access_token}`,
          },
          body: JSON.stringify({
            target_user_id: args.target_user_id,
            reason: args.reason,
            password: args.password,
            read_only: args.read_only ?? true,
            suppress_notification: args.suppress_notification ?? false,
            suppression_reason: args.suppression_reason,
          }),
        });
        if (!startRes.ok) {
          const body = await startRes.json().catch(() => ({}));
          throw new ImpersonationStartError(
            body?.detail ?? body?.error ?? `start-impersonation failed (${startRes.status})`,
            body?.error,
          );
        }
        const payload = (await startRes.json()) as {
          audit_id: string;
          token_hash: string;
          email: string;
        };

        const stash: ImpersonationStash = {
          audit_id: payload.audit_id,
          admin_access_token: adminSession.access_token,
          admin_refresh_token: adminSession.refresh_token,
          admin_user_id: adminSession.user.id,
          admin_email: adminSession.user.email,
          target_user_id: args.target_user_id,
          target_email: args.target_email,
          started_at: new Date().toISOString(),
          read_only: args.read_only ?? true,
          reason: args.reason,
        };
        writeImpersonationStash(stash);

        // Swap the browser's session into the target's. verifyOtp with the
        // magiclink token_hash consumes the one-time token and replaces
        // the active session.
        //
        // The token_hash variant accepts ONLY { token_hash, type }. Passing
        // `email` makes GoTrue reject with "Only the token_hash and type
        // should be provided" — the hashed token already identifies the
        // user server-side.
        const { error: otpErr } = await supabase.auth.verifyOtp({
          type: 'magiclink',
          token_hash: payload.token_hash,
        });
        if (otpErr) {
          clearImpersonationStash();
          // Best-effort: close the audit row we just opened so the admin
          // isn't blocked on retry. If this fails, the explicit "Cancel
          // previous attempt" button in the modal is the durable recovery
          // (it calls the same cancel-impersonation endpoint).
          try {
            await fetch(`${FUNCTIONS_BASE}/cancel-impersonation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${adminSession.access_token}`,
              },
              body: '{}',
            });
          } catch (cleanupErr) {
            captureError(cleanupErr as Error, { feature: 'impersonation-orphan-cleanup' });
          }
          throw otpErr;
        }

        // Drop cached query data from the admin's session — react-query
        // will refetch under the target's RLS.
        queryClient.clear();
      } finally {
        setIsStarting(false);
      }
    },
    [queryClient],
  );

  /**
   * Closes any open audit row owned by the current admin without doing a
   * session swap. Used to recover from a "started but never session-swapped"
   * orphan when start-impersonation returns 409 open_session_exists.
   */
  const cancel = useCallback(async () => {
    const { data: currentSession } = await supabase.auth.getSession();
    const adminAccessToken = currentSession?.session?.access_token;
    if (!adminAccessToken) {
      throw new Error('No active admin session.');
    }
    const res = await fetch(`${FUNCTIONS_BASE}/cancel-impersonation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminAccessToken}`,
      },
      body: '{}',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        body?.detail ?? body?.error ?? `cancel-impersonation failed (${res.status})`,
      );
    }
  }, []);

  const end = useCallback(async () => {
    setIsEnding(true);
    try {
      const stash = readImpersonationStash();
      if (!stash) {
        // Nothing to do.
        return;
      }
      const { data: currentSession } = await supabase.auth.getSession();
      const targetAccessToken = currentSession?.session?.access_token;

      // Best-effort close. If the target token is already invalid, the
      // sweep cron will close the row.
      if (targetAccessToken) {
        await fetch(`${FUNCTIONS_BASE}/end-impersonation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${targetAccessToken}`,
          },
          body: JSON.stringify({ audit_id: stash.audit_id, end_reason: 'admin_exit' }),
        }).catch((err) => {
          captureError(err as Error, { feature: 'impersonation-end-close' });
        });
      }

      // Restore admin's session. If the refresh token has expired the
      // admin has to re-login — clearer to surface that than to leave
      // them in a weird half-state.
      const { error: setErr } = await supabase.auth.setSession({
        access_token: stash.admin_access_token,
        refresh_token: stash.admin_refresh_token,
      });
      clearImpersonationStash();
      queryClient.clear();
      if (setErr) {
        // setSession failed: the target's session is still active in the
        // SDK, and clearing the stash + queryClient alone leaves the admin
        // trapped logged in as the target. Sign out before re-raising so
        // the caller can redirect to /login without AuthContext bouncing
        // the still-authenticated session back to /calendar.
        await supabase.auth.signOut().catch((soErr) => {
          captureError(soErr as Error, { feature: 'impersonation-end-forced-signout' });
        });
        throw setErr;
      }
    } finally {
      setIsEnding(false);
    }
  }, [queryClient]);

  return {
    state,
    start,
    end,
    cancel,
    isStarting,
    isEnding,
  };
}

/**
 * Read-only state hook for consumers that only need to know IF the
 * current session is being impersonated (e.g., mutation blockers in
 * deeply-nested components). Subscribes via storage events so a state
 * change anywhere in the app propagates.
 */
export function useImpersonationState(): ImpersonationState {
  const [state, setState] = useState<ImpersonationState>(() => {
    const stash = readImpersonationStash();
    if (!stash) return { active: false };
    return {
      active: true,
      audit_id: stash.audit_id,
      admin_email: stash.admin_email,
      target_email: stash.target_email,
      target_user_id: stash.target_user_id,
      started_at: stash.started_at,
      read_only: stash.read_only,
      reason: stash.reason,
    };
  });

  useEffect(() => {
    function recompute() {
      const stash = readImpersonationStash();
      if (!stash) {
        setState({ active: false });
        return;
      }
      setState({
        active: true,
        audit_id: stash.audit_id,
        admin_email: stash.admin_email,
        target_email: stash.target_email,
        target_user_id: stash.target_user_id,
        started_at: stash.started_at,
        read_only: stash.read_only,
        reason: stash.reason,
      });
    }
    const handler = () => recompute();
    window.addEventListener('storage', handler);
    // Listen for our own writes too — storage events don't fire in the
    // same tab. We poll on auth changes via supabase.auth subscribe.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      setTimeout(recompute, 0);
    });
    return () => {
      window.removeEventListener('storage', handler);
      subscription.unsubscribe();
    };
  }, []);

  return state;
}
